import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Route, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
  focusReturnState,
  takeReturnState,
  writeReturnState,
  type GridReturnState,
  type ReturnStateStorage,
} from '../../utils/grid-return-state.utils';

/**
 * Per-tab stash of a grid's state across a route editor round trip (WAP-2025):
 * the opener writes it, the editor names the row it was on, the grid takes it
 * once when it is entered again from under that editor. `sessionStorage`, so a
 * reload of the editor keeps it and another tab never sees it.
 */
@Injectable({ providedIn: 'root' })
export class GridReturnStateService {
  private readonly router = inject(Router);

  /** The last two completed navigations, newest first. */
  private readonly completed: string[] = [];

  /**
   * The editor URL of the last `remember`, read by the app's route reuse strategy
   * to detach the grid route only for that navigation (WAP-2025 slice 2).
   */
  rememberedEditorUrl: string | null = null;

  /** The grid route config just re-attached by that strategy; its directive puts the state back. */
  readonly reattached$ = new Subject<Route | null>();

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.completed.unshift(event.urlAfterRedirects);
        this.completed.length = Math.min(this.completed.length, 2);
      });
  }

  /**
   * The route the grid at `currentUrl` was entered from: the newest completed
   * navigation that is not its own. A grid's `ngOnInit` runs after its
   * `NavigationEnd`, and `router.currentNavigation()` is null by then.
   */
  enteredFrom(currentUrl: string): string | null {
    return this.completed.find((url) => url !== currentUrl) ?? null;
  }

  remember(viewId: string, state: GridReturnState): void {
    this.rememberedEditorUrl = state.editorUrl;
    this.withStorage((storage) => writeReturnState(storage, viewId, state));
  }

  take(viewId: string): GridReturnState | null {
    return (
      this.withStorage((storage) => takeReturnState(storage, viewId)) ?? null
    );
  }

  /** Called by the editor before it goes back: the row to scroll to and mark. */
  focus(rowId: string | number | null | undefined): void {
    if (rowId == null || rowId === '') {
      return;
    }
    this.withStorage((storage) => focusReturnState(storage, String(rowId)));
  }

  /** sessionStorage can throw (private window, blocked site data); the stash is then simply absent. */
  private withStorage<T>(
    action: (storage: ReturnStateStorage) => T,
  ): T | undefined {
    try {
      return action(sessionStorage);
    } catch {
      return undefined;
    }
  }
}
